/**
 * LINE LIFF初期化・認証のadapter境界(Phase「LIFF frontend実装」)。
 *
 * 正本(実装指示書 v2.0 11章「LIFF認証」):
 * - LIFFには`openid` scopeを必須設定(LIFFアプリ自体の設定、LINE Developers
 *   Console側。コードから強制することはできないため、README.mdへ明記する)。
 * - 画面側で取得したuser_idをサーバーで信用しない。
 * - `liff.getIDToken()`でID token取得。
 * - Workers側でLINE Verify ID Token API等により検証(既存backend、
 *   src/adapters/line/real.ts / src/auth/line-auth.ts)。
 * - ブラウザ側デコード値(liff.getDecodedIDToken()等)は本人確認情報として
 *   一切使わない。このモジュールもデコード済みclaimsを取得・提供しない
 *   (ID token文字列そのものだけをbackendへ渡す)。
 *
 * テスト容易性のため、実LIFF SDK(`@line/liff`)への依存をこのinterfaceの
 * 背後へ隔離する(FakeLiffClientでunit testから直接検証できるようにする)。
 */

export interface LiffClient {
  /** LIFFアプリを初期化する。openid scopeはLIFFアプリ設定側の責務。 */
  init(liffId: string): Promise<void>;
  isLoggedIn(): boolean;
  /** LINEログイン画面へ遷移する(LIFFブラウザ外ならLINEログインページを開く)。 */
  login(): void;
  /**
   * ID tokenを返す。ログイン前や取得失敗時はnull(呼び出し側はlogin()へ
   * 誘導する)。
   */
  getIDToken(): string | null;
  /** LINE内ブラウザ(LIFFブラウザ)で開かれているか。 */
  isInClient(): boolean;
  /** HTTPS URLを端末の外部ブラウザ(Safari等)で開く。 */
  openWindow(url: string, external: boolean): void;
}

export class LiffInitError extends Error {
  constructor(cause: unknown) {
    super(`LIFF init failed: ${String(cause)}`);
    this.name = "LiffInitError";
  }
}

/**
 * `@line/liff` SDKへの実接続。SDK自体のimportをこのファイル内へ閉じ込め、
 * main.ts等の呼び出し側はLiffClient interfaceだけに依存する。
 */
export class RealLiffClient implements LiffClient {
  private ready = false;

  async init(liffId: string): Promise<void> {
    const liffModule = await import("@line/liff");
    const liff = liffModule.default;
    try {
      await liff.init({ liffId });
    } catch (cause) {
      throw new LiffInitError(cause);
    }
    this.liff = liff;
    this.ready = true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private liff: any;

  private assertReady(): void {
    if (!this.ready || !this.liff) {
      throw new Error("LiffClient.init() must succeed before calling this method");
    }
  }

  isLoggedIn(): boolean {
    this.assertReady();
    return Boolean(this.liff.isLoggedIn());
  }

  login(): void {
    this.assertReady();
    this.liff.login();
  }

  getIDToken(): string | null {
    this.assertReady();
    const token = this.liff.getIDToken();
    return typeof token === "string" && token.length > 0 ? token : null;
  }

  isInClient(): boolean {
    this.assertReady();
    return Boolean(this.liff.isInClient());
  }

  openWindow(url: string, external: boolean): void {
    this.assertReady();
    this.liff.openWindow({ url, external });
  }
}
